import React, { useState, useContext } from 'react';
import { observer } from 'mobx-react';
import { format, formatDistanceToNow } from 'date-fns';
import Card from 'src/components/Card';
import Flex from 'src/components/Flex';
import Table from 'src/components/Table';
import Button from 'src/components/Button';
import CenterModal from 'src/components/CenterModal';
import ModalHeader from 'src/components/ModalHeader';
import ModalFooter from 'src/components/ModalFooter';
import Checkbox from 'src/components/Checkbox';
import Input from 'src/components/Input';
import Toggle from 'src/components/Toggle';
import NavBar from 'src/containers/NavBar';
import PageContainer from 'src/components/PageContainer';
import PageContent from 'src/components/PageContent';
import FacilityDropDown from 'src/components/FacilityDropDown';
import TextTooltip from 'src/components/TextTooltip';
import UserStoreContext from 'src/stores/UserStore';
import FacilityStoreContext from 'src/stores/FacilityStore';
import { ToastStoreObject, ToastType } from 'src/stores/ToastStore';
import { parseError } from 'src/utils';
import { DISPLAY_DATE_FORMAT } from 'src/utils/constants';
import { useApiKeys, useApiKeyScopes, useCreateApiKey, useRevokeApiKey } from 'src/queries/useApiKeys';
import { CreateApiKeyData, CreateApiKeyResponse } from 'src/api/apiKeys';
import { CellInfo } from 'react-table';
import styles from './index.module.scss';

/** Links the disabled Create button to the reason shown beside it, for screen readers. */
const CREATE_REASON_ID = 'api-key-create-unavailable-reason';

const ApiKeys = observer(function ApiKeys() {
    const userStore = useContext(UserStoreContext);
    const facilityStore = useContext(FacilityStoreContext);
    const { data: apiKeys, isLoading } = useApiKeys();
    // The scopes on offer come from the server, which intersects what a live route implements with
    // what this org's feature flags enable. The dashboard used to keep its own copy of the full
    // list and pre-select all of it, which offered permissions no route enforced.
    const { data: scopeData, isError: isScopeError, refetch: refetchScopes } = useApiKeyScopes();

    // Three states have to stay distinct, and `scopeData ?? []` collapsed all three into one empty
    // array: the request is in flight, the request failed, and the organization genuinely has
    // nothing on offer. Reporting the first two as the third told the user their API access was
    // switched off -- alarming, wrong, and it sends whoever is on call to the flag console instead
    // of the outage.
    //
    // Keyed on `data !== undefined` rather than on isLoading/isError because that is the only
    // question the rest of this component asks: do we know the list? It also keeps a cached list
    // usable if a later refetch fails, where isError alone would discard it.
    const knowsOfferableScopes = scopeData !== undefined;
    const offerableScopes = scopeData ?? [];
    const offerableScopeSet = new Set(offerableScopes);
    const hasNoOfferableScopes = knowsOfferableScopes && offerableScopes.length === 0;
    const couldNotLoadScopes = isScopeError && !knowsOfferableScopes;
    // Nothing to grant, or nothing known to grant. Either way opening the modal would show an empty
    // scope list and every submission would be refused.
    const canCreateKey = knowsOfferableScopes && offerableScopes.length > 0;

    const canManageKeys = userStore.userPermissions.canCreate.apiKeys;
    // Only one reason is ever rendered, so one id is enough to point `aria-describedby` at it.
    const showCreateReason = canManageKeys && (couldNotLoadScopes || hasNoOfferableScopes);

    const createApiKey = useCreateApiKey();
    const revokeApiKey = useRevokeApiKey();

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showRevealModal, setShowRevealModal] = useState(false);
    const [showRevokeConfirm, setShowRevokeConfirm] = useState<number | null>(null);
    const [newKeyResponse, setNewKeyResponse] = useState<CreateApiKeyResponse | null>(null);
    const [copied, setCopied] = useState(false);

    const [scopeToFacilities, setScopeToFacilities] = useState(false);
    const [selectedFacilityIds, setSelectedFacilityIds] = useState<number[]>([]);

    // Seeded when the modal opens rather than at mount: the offerable list arrives asynchronously,
    // so there is nothing to pre-select on the first render.
    const [createForm, setCreateForm] = useState<CreateApiKeyData>({ label: '', scopes: [] });

    function openCreateModal() {
        setCreateForm({ label: '', scopes: [...offerableScopes] });
        setScopeToFacilities(false);
        setSelectedFacilityIds([]);
        setShowCreateModal(true);
    }

    const columns = [
        {
            Header: 'PREFIX',
            accessor: 'prefix',
            minWidth: 80,
            Cell: (props: CellInfo) => <code>{props.value}</code>,
        },
        {
            Header: 'LABEL',
            accessor: 'label',
            minWidth: 160,
        },
        {
            Header: 'SCOPES',
            accessor: 'scopes',
            minWidth: 160,
            Cell: (props: CellInfo) => {
                const scopes = props.value as string[];

                // Gated on the list being known. Without this, an in-flight or failed request left
                // the offerable set empty, so every scope on every key rendered struck through and
                // the tooltip claimed they had all stopped working -- the exact opposite of the
                // diagnosis the marking exists to support.
                const isUnavailable = (scope: string) => knowsOfferableScopes && !offerableScopeSet.has(scope);

                // "All" means "every scope currently on offer, and nothing else". A key minted
                // earlier can hold more than that -- a write scope, say -- and collapsing it to
                // "All" would hide exactly the scopes that no longer work.
                const isEveryOfferableScope =
                    offerableScopes.length > 0 &&
                    scopes.length === offerableScopes.length &&
                    scopes.every((s) => offerableScopeSet.has(s));
                if (isEveryOfferableScope) return <span>All</span>;

                const unavailable = scopes.filter(isUnavailable);
                const text = scopes.join(', ');
                const tooltip = unavailable.length
                    ? `${text} — not currently enabled: ${unavailable.join(', ')}`
                    : text;

                return (
                    <TextTooltip text={tooltip}>
                        <span className={styles.truncatedCell}>
                            {scopes.map((scope, i) => (
                                <React.Fragment key={scope}>
                                    {i > 0 && ', '}
                                    {/* Marked rather than hidden: the key really does carry this
                                        scope, and an operator looking at a 403 needs to see which
                                        one is no longer enabled. */}
                                    <span className={isUnavailable(scope) ? styles.unavailableScope : undefined}>
                                        {scope}
                                    </span>
                                </React.Fragment>
                            ))}
                        </span>
                    </TextTooltip>
                );
            },
        },
        {
            Header: 'FACILITIES',
            accessor: 'facilityIds',
            minWidth: 160,
            Cell: (props: CellInfo) => {
                const ids = props.value as number[] | null;
                if (!ids) return <span>All</span>;
                const names = ids.map((id) => facilityStore.findFacility(id)?.facNm ?? `#${id}`);
                const text = names.join(', ');
                return (
                    <TextTooltip text={text}>
                        <span className={styles.truncatedCell}>{text}</span>
                    </TextTooltip>
                );
            },
        },
        {
            Header: 'LAST USED',
            accessor: 'lastUsedAt',
            minWidth: 120,
            Cell: (props: CellInfo) =>
                props.value ? (
                    <span>{formatDistanceToNow(new Date(props.value), { addSuffix: true })}</span>
                ) : (
                    <span className={styles.muted}>Never</span>
                ),
        },
        {
            Header: 'CREATED',
            accessor: 'createdAt',
            minWidth: 100,
            Cell: (props: CellInfo) => <span>{format(new Date(props.value), DISPLAY_DATE_FORMAT)}</span>,
        },
        {
            Header: 'ACTIONS',
            minWidth: 100,
            Cell: (props: CellInfo) => (
                <Button
                    type="secondary"
                    text="Revoke"
                    isLocked={!canManageKeys}
                    onClick={() => setShowRevokeConfirm(props.original.apiKeyId)}
                />
            ),
        },
    ];

    async function handleCreate() {
        if (!createForm.label.trim()) {
            ToastStoreObject.show('Label is required', ToastType.Warn);
            return;
        }
        // Filtered rather than sent as held. A scope that left the offerable list while the modal
        // was open has no checkbox, so the user could not deselect it -- and the server refuses the
        // whole request over it, leaving them stuck with no way out. Narrow to what is on offer and
        // that is unreachable.
        const scopes = createForm.scopes.filter((s) => offerableScopeSet.has(s));
        if (scopes.length === 0) {
            ToastStoreObject.show('At least one scope is required', ToastType.Warn);
            return;
        }
        if (scopeToFacilities && selectedFacilityIds.length === 0) {
            ToastStoreObject.show('Select at least one facility or uncheck facility restriction', ToastType.Warn);
            return;
        }

        try {
            const payload: CreateApiKeyData = {
                ...createForm,
                scopes,
                facilityIds: scopeToFacilities ? selectedFacilityIds : null,
            };
            const response = await createApiKey.mutateAsync(payload);
            setNewKeyResponse(response);
            setShowCreateModal(false);
            setShowRevealModal(true);
            // Cleared rather than re-seeded; `openCreateModal` re-seeds from the offerable list, so
            // a flag flipped since this page loaded is picked up next time.
            setCreateForm({ label: '', scopes: [] });
            setScopeToFacilities(false);
            setSelectedFacilityIds([]);
            ToastStoreObject.show('API key created', ToastType.Success);
        } catch (e) {
            ToastStoreObject.show(parseError(e));
        }
    }

    async function handleRevoke(apiKeyId: number) {
        try {
            await revokeApiKey.mutateAsync(apiKeyId);
            setShowRevokeConfirm(null);
            ToastStoreObject.show('API key revoked', ToastType.Success);
        } catch (e) {
            ToastStoreObject.show(parseError(e));
        }
    }

    function toggleScope(scope: string) {
        setCreateForm((prev) => ({
            ...prev,
            scopes: prev.scopes.includes(scope) ? prev.scopes.filter((s) => s !== scope) : [...prev.scopes, scope],
        }));
    }

    // Compared as sets, not by length. Both sides come from the same server list today, so equal
    // length does imply equal content -- but only because the filter in handleCreate makes a
    // mismatch unreachable. Stating the intent directly means this survives that changing.
    const allOfferableSelected =
        offerableScopes.length > 0 && offerableScopes.every((s) => createForm.scopes.includes(s));

    function toggleAllScopes() {
        setCreateForm((prev) => ({
            ...prev,
            scopes: allOfferableSelected ? [] : [...offerableScopes],
        }));
    }

    async function copyToClipboard() {
        if (newKeyResponse?.plaintext) {
            await navigator.clipboard.writeText(newKeyResponse.plaintext);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    }

    return (
        <PageContainer>
            <NavBar />
            <PageContent>
                <Flex direction="row" align="center" justify="between" className={styles.header}>
                    <h2>API Keys</h2>
                    <Flex direction="row" align="center" gap={8}>
                        {/* Shown only to a user who could otherwise create a key. Without this, a
                            user lacking the permission saw a padlock *and* a message about the
                            organization -- two different reasons for one disabled control, neither
                            of which they can act on. */}
                        {canManageKeys && couldNotLoadScopes && (
                            <>
                                {/* Distinct from "not enabled". A failed request means we do not
                                    know, and saying the feature is off would misdiagnose an outage
                                    as a deliberate change. */}
                                <span id={CREATE_REASON_ID} className={styles.createUnavailableReason}>
                                    Could not load available scopes
                                </span>
                                <Button type="small" text="Retry" onClick={() => refetchScopes()} />
                            </>
                        )}
                        {canManageKeys && hasNoOfferableScopes && (
                            <span id={CREATE_REASON_ID} className={styles.createUnavailableReason}>
                                API access is not enabled for this organization yet
                            </span>
                        )}
                        <Button
                            type="primary"
                            text="Create API Key"
                            isLocked={!canManageKeys}
                            // Disabled rather than locked: this is not a permission problem, it is
                            // that there is nothing grantable -- or nothing known to be grantable
                            // while the request is in flight or after it failed. Opening the modal
                            // would show an empty scope list and every submission would be refused.
                            disabled={!canCreateKey}
                            // Ties the button to whichever reason is displayed, so a screen reader
                            // reaching a disabled control is told why rather than left guessing.
                            aria-describedby={showCreateReason ? CREATE_REASON_ID : undefined}
                            onClick={openCreateModal}
                        />
                    </Flex>
                </Flex>

                <Card>
                    <Table
                        data={apiKeys || []}
                        columns={columns}
                        loading={isLoading}
                        defaultPageSize={20}
                        noDataText="No API keys found"
                    />
                </Card>

                {/* Create Modal */}
                <CenterModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}>
                    <ModalHeader title="Create API Key" onClose={() => setShowCreateModal(false)} />
                    <div className={styles.modalBody}>
                        <div className={styles.field}>
                            <label>Label</label>
                            <Input
                                value={createForm.label}
                                onChange={(e: any) =>
                                    setCreateForm((prev) => ({ ...prev, label: e.target.value }))
                                }
                                placeholder="e.g., Graphium AI Production"
                            />
                        </div>
                        <div className={styles.field}>
                            <Flex direction="row" align="center" justify="between">
                                <label>Scopes</label>
                                <Button
                                    type="small"
                                    text={allOfferableSelected ? 'Deselect All' : 'Select All'}
                                    onClick={toggleAllScopes}
                                />
                            </Flex>
                            <div className={styles.scopeGrid}>
                                {offerableScopes.map((scope) => (
                                    <Checkbox
                                        key={scope}
                                        checked={createForm.scopes.includes(scope)}
                                        onChange={() => toggleScope(scope)}
                                        label={scope}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className={styles.field}>
                            <Flex direction="row" align="center" gap={8}>
                                <Toggle
                                    ariaLabel="Restrict API key to specific facilities"
                                    toggled={scopeToFacilities}
                                    onChange={(isToggled: boolean) => {
                                        setScopeToFacilities(isToggled);
                                        if (!isToggled) setSelectedFacilityIds([]);
                                    }}
                                />
                                <label>Restrict to specific facilities</label>
                            </Flex>
                            {scopeToFacilities && (
                                <div style={{ marginTop: 8 }}>
                                    <FacilityDropDown
                                        isMulti
                                        allowSelectAll
                                        closeMenuOnSelect={false}
                                        selectedValue={selectedFacilityIds}
                                        placeholder="Select facilities..."
                                        onChange={(selected: any) => {
                                            const ids = (selected || []).map((f: any) => f.value);
                                            setSelectedFacilityIds(ids);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <ModalFooter
                        primaryText="Create"
                        primaryClick={handleCreate}
                        secondaryText="Cancel"
                        secondaryClick={() => setShowCreateModal(false)}
                    />
                </CenterModal>

                {/* Key Reveal Modal */}
                <CenterModal isOpen={showRevealModal && !!newKeyResponse} onClose={() => setShowRevealModal(false)}>
                    <ModalHeader title="API Key Created" onClose={() => setShowRevealModal(false)} />
                    <div className={styles.modalBody}>
                        <div className={styles.warning}>
                            This key will only be shown once. Copy it now.
                        </div>
                        <div className={styles.keyDisplay}>
                            <code className={styles.keyText}>{newKeyResponse?.plaintext}</code>
                            <Button
                                type="secondary"
                                text={copied ? 'Copied!' : 'Copy'}
                                onClick={copyToClipboard}
                            />
                        </div>
                    </div>
                    <ModalFooter
                        primaryText="Done"
                        primaryClick={() => {
                            setShowRevealModal(false);
                            setNewKeyResponse(null);
                            setCopied(false);
                        }}
                    />
                </CenterModal>

                {/* Revoke Confirmation Modal */}
                <CenterModal isOpen={showRevokeConfirm !== null} onClose={() => setShowRevokeConfirm(null)}>
                    <ModalHeader title="Revoke API Key" onClose={() => setShowRevokeConfirm(null)} />
                    <div className={styles.modalBody}>
                        <p>
                            Are you sure you want to revoke this API key? Any integrations using this key will
                            immediately lose access.
                        </p>
                    </div>
                    <ModalFooter
                        primaryText="Revoke"
                        primaryClick={() => showRevokeConfirm !== null && handleRevoke(showRevokeConfirm)}
                        secondaryText="Cancel"
                        secondaryClick={() => setShowRevokeConfirm(null)}
                    />
                </CenterModal>
            </PageContent>
        </PageContainer>
    );
});

export default ApiKeys;
